import api from './api';

export const dashboardService = {
  // 1. Phlebotomy / Sample Collection Metrics
  getPhlebotomySummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/sample-collection/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch phlebotomy summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 2. Sample Receive / Accessioning Metrics
  getAccessionSummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/sample-receive/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch accession summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 3. Pathology Results Worklist Metrics
  getPathologySummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/pathology-results/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch pathology summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 4. Report Approval Queue Metrics
  getApprovalSummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/report-approval/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch report approval summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 5. Billing & Due Settlement Metrics
  getSettlementMetrics: async (centreId = null) => {
    try {
      const params = { page: 1, pageSize: 1 };
      if (centreId) params.centreId = centreId;
      const res = await api.get('/settlements/due-bills', { params });
      const payload = res.data?.data ?? res.data;
      const metrics = payload?.metrics || {};
      return {
        success: true,
        data: {
          ...payload,
          totalDueAmount: metrics.totalDueAmount ?? payload.totalDueAmount ?? 0,
          unpaidCount: metrics.unpaidCount ?? payload.unpaidCount ?? 0,
          creditCount: metrics.creditCount ?? 0,
          fullPaidCount: metrics.fullPaidCount ?? 0,
          totalBillsCount: payload.totalCount ?? 0,
          metrics
        }
      };
    } catch (err) {
      console.error('Failed to fetch settlement metrics:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 6. Radiology Metrics
  getRadiologySummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/radiology/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch radiology summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 7. Recent Registrations & Patient Flow (Real Data)
  getRecentRegistrations: async (centreId = null, limit = 6) => {
    try {
      const params = { page: 1, pageSize: limit };
      if (centreId) params.centreId = centreId;
      const res = await api.get('/registrations', { params });
      const payload = res.data?.data ?? res.data;
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      const totalCount = Array.isArray(payload) ? payload.length : (payload?.totalCount || items.length);
      return {
        success: true,
        data: {
          items,
          totalCount
        }
      };
    } catch (err) {
      console.error('Failed to fetch recent registrations:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 8. Security & Access Control Stats (Admin Only)
  getSecurityStats: async () => {
    try {
      const res = await api.get('/permissions/summary-stats');
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch permission stats:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 9. Outsource Laboratory Workload Metrics
  getOutsourceSummary: async (centreId = null) => {
    try {
      const params = centreId ? { centreId } : {};
      const res = await api.get('/outsource/summary', { params });
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch outsource summary:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // 10. System & Database Health Metrics
  getSystemHealth: async () => {
    try {
      const res = await api.get('/health');
      return { success: true, data: res.data?.data ?? res.data };
    } catch (err) {
      console.error('Failed to fetch system health:', err);
      return { success: false, error: err.message, status: 'ERROR' };
    }
  },

  // Batch Loader for Dashboard (Parallel Execution with Center Isolation)
  loadAllDashboardData: async (centreId = null) => {
    const [
      phlebotomy,
      accession,
      pathology,
      approval,
      settlement,
      radiology,
      recentRegistrations,
      security,
      outsource,
      health
    ] = await Promise.all([
      dashboardService.getPhlebotomySummary(centreId),
      dashboardService.getAccessionSummary(centreId),
      dashboardService.getPathologySummary(centreId),
      dashboardService.getApprovalSummary(centreId),
      dashboardService.getSettlementMetrics(centreId),
      dashboardService.getRadiologySummary(centreId),
      dashboardService.getRecentRegistrations(centreId, 6),
      dashboardService.getSecurityStats(),
      dashboardService.getOutsourceSummary(centreId),
      dashboardService.getSystemHealth()
    ]);

    return {
      phlebotomy: phlebotomy.success ? phlebotomy.data : null,
      accession: accession.success ? accession.data : null,
      pathology: pathology.success ? pathology.data : null,
      approval: approval.success ? approval.data : null,
      settlement: settlement.success ? settlement.data : null,
      radiology: radiology.success ? radiology.data : null,
      recentRegistrations: recentRegistrations.success ? recentRegistrations.data : null,
      security: security.success ? security.data : null,
      outsource: outsource.success ? outsource.data : null,
      health: health.success ? health.data : null,
      loadedAt: new Date().toISOString()
    };
  }
};

export default dashboardService;
